import * as React from "react";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import {
  isMobileOffer,
  useOffer,
  useOfferMode,
} from "@nju/data-access-portal-offers";
import { useOrchestrators } from "@nju/data-access-portal-orchestrator";
import {
  pushAddToCartEventGA4,
  pushBeginCheckoutEventGA4,
  pushSimpleEvent,
} from "@nju/data-layer";
import { redirectToOrchestratorState } from "@nju/feature-portal-orchestrator";
import { SidebarOffer } from "@nju/feature-portal-sidebar";
import { redirectToOrchestratorState as redirectToSalesOrchestratorState } from "@nju/feature-sales-orchestrator";
import { CancelOrderModal } from "@nju/feature-sales-order";
import { portalRoutes, salesRoutes } from "@nju/routes";
import { IScope } from "@nju/scope";
import {
  BlockContent,
  CircleLoader,
  GeneralErrorNotification,
  Layout,
  Main,
  Modal,
  Side,
  useOverlay,
} from "@nju/ui";
import { OnboardingMembersInfo } from "../OnboardingMembersInfo";
import {
  ChooseNewNumberExpired,
  ChooseNewNumberForm,
  ChooseNewNumberNoResults,
} from "./form";
import { chooseNewNumberMachine } from "./form/chooseNewNumber.machine";

interface IChooseNewNumberPageProps {
  variant?: IScope;
}

export function ChooseNewNumberPage({
  variant = "portal",
}: IChooseNewNumberPageProps) {
  const mode = useOfferMode();
  const router = useRouter();
  const cancelOrderModal = useOverlay();
  const { multiOrder } = useOrchestrators();
  const offer = useOffer(router.query.oferta as string);
  const finishLaterHref = portalRoutes.mojeKonto.index.get({
    mode,
    flow: "multiOrder",
  });

  const nextStepUrl =
    variant !== "sales"
      ? portalRoutes.idziemyDalej.get({
          flow: "multiOrder",
          screenVariant: "success",
        })
      : salesRoutes.idziemyDalej.get({
          screenVariant: "success",
        });

  const [current, send] = useMachine(chooseNewNumberMachine, {
    actions: {
      onSuccess: (_, event) => {
        if (event.type === "DONE") {
          if (
            multiOrder?.currentMultiOrderItem !==
            multiOrder?.multiOrderItemsCount
          ) {
            return router.push(nextStepUrl);
          } else {
            if (variant !== "sales") {
              return redirectToOrchestratorState({
                state: event.orchestrator.state,
                mode,
              });
            }
            if (variant === "sales") {
              return redirectToSalesOrchestratorState({
                state: event.orchestrator.state,
                mode: "mobile",
              });
            }
          }
        }
      },
      redirectToAnotherOrderExists: () => {
        router.push(portalRoutes.zamowienieWTrakcie.get());
      },
    },
    context: {
      offerId: offer?.offerId,
      variant,
    },
  });

  const { numbers, selectedNumber, errorMessage } = current.context;
  const { matches } = current;

  React.useLayoutEffect(() => {
    if (offer?.state === "ARCHIVED") {
      router.push(portalRoutes.komorkowy.get());
    }
  }, [offer?.state, router]);

  const handleFinishLater = React.useCallback(() => {
    pushSimpleEvent({
      event: "activity_service",
      eventName: "process_finish_later",
    });

    router.push(finishLaterHref);
  }, [router, finishLaterHref]);

  const handleFetchNewNumbers = React.useCallback(() => {
    send({ type: "FETCH_NEW_NUMBERS" });
  }, [send]);

  const handleSetSelectedNumber = React.useCallback(
    (number: string) => {
      send({ type: "SET_SELECTED_NUMBER", value: number });
    },
    [send],
  );

  const handleSubmit = React.useCallback(() => {
    if (isMobileOffer(offer) && multiOrder) {
      const { currentMultiOrderItem, multiOrderItemsCount, multiOrderItems } =
        multiOrder;

      if (multiOrderItems) {
        pushAddToCartEventGA4({
          productId: offer.offerId,
          numberCount: multiOrderItemsCount as number,
          productPrice: multiOrderItems[0]?.orderSummary
            ?.totalRecurringOrderPrice as number,
          subscriptionCategory: "Nowy",
        });
      }

      if (multiOrderItems && currentMultiOrderItem === multiOrderItemsCount) {
        pushBeginCheckoutEventGA4({
          productId: offer.offerId,
          numberCount: multiOrderItemsCount as number,
          productPrice: multiOrderItems[0]?.orderSummary
            ?.totalRecurringOrderPrice as number,
          offerCategory: "Subskrypcja",
          subscriptionCategory: "Nowy",
        });
      }
    }

    send({ type: "SUBMIT" });
  }, [send, offer, multiOrder]);

  if (matches("expiredScreen")) {
    return (
      <BlockContent grid="narrow" spacing="xl">
        <ChooseNewNumberExpired onSubmit={handleFetchNewNumbers} />
      </BlockContent>
    );
  }

  if (matches("noDataScreen")) {
    return (
      <BlockContent grid="narrow" spacing="xl">
        <ChooseNewNumberNoResults onSubmit={handleFetchNewNumbers} />
      </BlockContent>
    );
  }

  if (matches("loading")) {
    return (
      <div className="flex justify-center items-center h-full py-10">
        <CircleLoader />
      </div>
    );
  }

  if (matches("error")) {
    return (
      <GeneralErrorNotification
        callback={() => {
          return window.location.reload();
        }}
      />
    );
  }

  return (
    <>
      {variant !== "sales" && (
        <OnboardingMembersInfo isMulti={false} multiOrder={multiOrder} />
      )}
      <Layout>
        <Main>
          <ChooseNewNumberForm
            errorMessage={errorMessage}
            flow="NEW_NUMBER"
            handleFetchNewNumbers={handleFetchNewNumbers}
            handleSetSelectedNumber={handleSetSelectedNumber}
            handleSubmit={handleSubmit}
            isLoading={matches("loading")}
            isLoadingSubmit={
              matches("checkOrchestrator") ||
              matches("newOrder") ||
              matches("redirectToDeliveryAddressPage")
            }
            listOfNumbers={numbers}
            onFinishLater={handleFinishLater}
            openModal={cancelOrderModal.open}
            selectedNumber={selectedNumber}
            variant={variant}
          />
        </Main>
        <Side>
          <SidebarOffer
            offer={offer}
            orchestrator={multiOrder}
            variant={variant}
          />
        </Side>
        {multiOrder && cancelOrderModal.isOpened && (
          <Modal onClose={cancelOrderModal.close} title="anulowanie zamówienia">
            <CancelOrderModal
              mode="mobile"
              onClose={cancelOrderModal.close}
              orchestratorId={multiOrder.id}
            />
          </Modal>
        )}
      </Layout>
    </>
  );
}
